import React, { createContext, useContext, useReducer, useCallback, useRef } from 'react';
import { useWebSocket, type ConnectionState } from './useWebSocket';

// Types
export interface Message {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  isStreaming?: boolean;
}

export interface Conversation {
  id: string;
  title: string | null;
  status: 'active' | 'ended';
  messageCount: number;
  lastMessagePreview: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ToolCall {
  id: string;
  name: string;
  serverName?: string;
}

interface ChatState {
  conversations: Conversation[];
  activeConversation: Conversation | null;
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  isTyping: boolean;
  activeToolCall: ToolCall | null;
}

type ChatAction =
  | { type: 'SET_CONVERSATIONS'; conversations: Conversation[] }
  | { type: 'SET_ACTIVE_CONVERSATION'; conversation: Conversation | null }
  | { type: 'ADD_CONVERSATION'; conversation: Conversation }
  | { type: 'ADD_CONVERSATION_KEEP_MESSAGES'; conversation: Conversation }
  | { type: 'SET_MESSAGES'; messages: Message[] }
  | { type: 'ADD_MESSAGE'; message: Message }
  | { type: 'UPDATE_MESSAGE'; messageId: string; content: string; isStreaming?: boolean }
  | { type: 'APPEND_MESSAGE_DELTA'; messageId: string; delta: string }
  | { type: 'UPDATE_PENDING_MESSAGES_CONVERSATION'; conversationId: string }
  | { type: 'SYNC_USER_MESSAGE'; messageId: string; conversationId: string }
  | { type: 'SYNC_USER_MESSAGE_BY_TEMP_ID'; tempId: string; messageId: string; conversationId: string }
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_TYPING'; isTyping: boolean }
  | { type: 'SET_ACTIVE_TOOL_CALL'; toolCall: ToolCall | null };

const initialState: ChatState = {
  conversations: [],
  activeConversation: null,
  messages: [],
  isLoading: false,
  error: null,
  isTyping: false,
  activeToolCall: null,
};

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.conversations };

    case 'SET_ACTIVE_CONVERSATION':
      return { ...state, activeConversation: action.conversation };

    case 'ADD_CONVERSATION':
      return {
        ...state,
        conversations: [action.conversation, ...state.conversations],
        activeConversation: action.conversation,
      };

    case 'ADD_CONVERSATION_KEEP_MESSAGES':
      // Add conversation but keep existing messages (for when sending from Home)
      return {
        ...state,
        conversations: [action.conversation, ...state.conversations],
        activeConversation: action.conversation,
        // Update any messages with empty conversationId to use the new conversation
        messages: state.messages.map((m) =>
          m.conversationId === '' ? { ...m, conversationId: action.conversation.id } : m
        ),
      };

    case 'SET_MESSAGES':
      return { ...state, messages: action.messages };

    case 'ADD_MESSAGE':
      console.log('[Chat] ADD_MESSAGE:', action.message.role, action.message.id, 'content length:', action.message.content.length);
      return { ...state, messages: [...state.messages, action.message] };

    case 'UPDATE_MESSAGE':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.messageId
            ? { ...m, content: action.content, isStreaming: action.isStreaming }
            : m
        ),
      };

    case 'APPEND_MESSAGE_DELTA':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.messageId
            ? { ...m, content: m.content + action.delta, isStreaming: true }
            : m
        ),
      };

    case 'UPDATE_PENDING_MESSAGES_CONVERSATION':
      // Update messages with empty conversationId to use the provided conversationId
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.conversationId === '' ? { ...m, conversationId: action.conversationId } : m
        ),
      };

    case 'SYNC_USER_MESSAGE':
      // Find the most recent temp user message and sync its ID with the server's ID
      // This ensures the message persists correctly when the conversation is reloaded
      const tempUserMessages = state.messages.filter(
        (m) => m.role === 'user' && m.id.startsWith('temp_')
      );
      console.log('[Chat] SYNC_USER_MESSAGE - temp messages:', tempUserMessages.length, 'total messages:', state.messages.length);

      if (tempUserMessages.length === 0) {
        console.log('[Chat] No temp messages to sync');
        return state;
      }

      // Get the most recent temp message (last one)
      const messageToSync = tempUserMessages[tempUserMessages.length - 1];
      console.log('[Chat] Syncing message:', messageToSync.id, '→', action.messageId);

      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === messageToSync.id
            ? { ...m, id: action.messageId, conversationId: action.conversationId }
            : m
        ),
      };

    case 'SYNC_USER_MESSAGE_BY_TEMP_ID':
      // Sync a specific temp message by its temp ID (more reliable than searching state)
      console.log('[Chat] SYNC_USER_MESSAGE_BY_TEMP_ID:', action.tempId, '→', action.messageId);
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.tempId
            ? { ...m, id: action.messageId, conversationId: action.conversationId }
            : m
        ),
      };

    case 'SET_LOADING':
      return { ...state, isLoading: action.isLoading };

    case 'SET_ERROR':
      return { ...state, error: action.error };

    case 'SET_TYPING':
      return { ...state, isTyping: action.isTyping };

    case 'SET_ACTIVE_TOOL_CALL':
      return { ...state, activeToolCall: action.toolCall };

    default:
      return state;
  }
}

// Context
interface ChatContextValue extends ChatState {
  connectionState: ConnectionState;
  sendMessage: (content: string) => void;
  startNewConversation: () => void;
  selectConversation: (conversationId: string) => void;
  endConversation: () => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

// Provider
interface ChatProviderProps {
  children: React.ReactNode;
  stackId: string;
  customerId: string;
  apiKey?: string;
}

export function ChatProvider({ children, stackId, customerId, apiKey: _apiKey }: ChatProviderProps) {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const streamingMessageRef = useRef<string | null>(null);
  // Track pending temp messages to handle race conditions with sync
  const pendingTempMessagesRef = useRef<Map<string, { tempId: string; content: string }>>(new Map());

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data: Record<string, unknown>) => {
    switch (data.type) {
      case 'connected':
        dispatch({ type: 'SET_CONVERSATIONS', conversations: (data.conversations || []) as Conversation[] });
        break;

      case 'conversations':
        dispatch({ type: 'SET_CONVERSATIONS', conversations: (data.data || []) as Conversation[] });
        break;

      case 'conversation':
        const convData = data.data as { conversation: Conversation; messages: Message[] };
        console.log('[Chat] Loading conversation:', convData.conversation.id, 'with', convData.messages.length, 'messages');
        console.log('[Chat] First message:', convData.messages[0]);
        dispatch({ type: 'SET_ACTIVE_CONVERSATION', conversation: convData.conversation });
        dispatch({ type: 'SET_MESSAGES', messages: convData.messages });
        break;

      case 'conversation_started':
        // Use ADD_CONVERSATION_KEEP_MESSAGES to preserve any pending messages (e.g., from Home page)
        dispatch({ type: 'ADD_CONVERSATION_KEEP_MESSAGES', conversation: data.data as Conversation });
        break;

      case 'message_received':
        dispatch({ type: 'SET_TYPING', isTyping: true });
        // Sync the user message ID with the server's ID so it persists correctly
        if (data.messageId && data.conversationId) {
          // First try using the ref (handles race condition)
          const pending = Array.from(pendingTempMessagesRef.current.values());
          if (pending.length > 0) {
            const lastPending = pending[pending.length - 1];
            console.log('[Chat] Syncing from ref:', lastPending.tempId, '→', data.messageId);
            dispatch({
              type: 'SYNC_USER_MESSAGE_BY_TEMP_ID',
              tempId: lastPending.tempId,
              messageId: data.messageId as string,
              conversationId: data.conversationId as string,
            });
            pendingTempMessagesRef.current.delete(lastPending.tempId);
          } else {
            // Fallback to state-based sync
            dispatch({
              type: 'SYNC_USER_MESSAGE',
              messageId: data.messageId as string,
              conversationId: data.conversationId as string,
            });
          }
        }
        break;

      case 'message_start':
        streamingMessageRef.current = data.messageId as string;
        dispatch({
          type: 'ADD_MESSAGE',
          message: {
            id: data.messageId as string,
            conversationId: data.conversationId as string,
            role: 'assistant',
            content: '',
            createdAt: new Date().toISOString(),
            isStreaming: true,
          },
        });
        break;

      case 'message_delta':
        if (streamingMessageRef.current === data.messageId) {
          dispatch({
            type: 'APPEND_MESSAGE_DELTA',
            messageId: data.messageId as string,
            delta: data.delta as string,
          });
        }
        break;

      case 'message_complete':
        streamingMessageRef.current = null;
        dispatch({
          type: 'UPDATE_MESSAGE',
          messageId: data.messageId as string,
          content: data.content as string,
          isStreaming: false,
        });
        dispatch({ type: 'SET_TYPING', isTyping: false });
        dispatch({ type: 'SET_LOADING', isLoading: false });
        dispatch({ type: 'SET_ACTIVE_TOOL_CALL', toolCall: null });
        break;

      case 'tool_call_start':
        const toolCall = data.toolCall as { id: string; name: string; serverName?: string };
        dispatch({
          type: 'SET_ACTIVE_TOOL_CALL',
          toolCall: {
            id: toolCall.id,
            name: toolCall.name,
            serverName: toolCall.serverName,
          },
        });
        break;

      case 'tool_call_complete':
        dispatch({ type: 'SET_ACTIVE_TOOL_CALL', toolCall: null });
        break;

      case 'error':
        dispatch({ type: 'SET_ERROR', error: data.message as string });
        dispatch({ type: 'SET_TYPING', isTyping: false });
        dispatch({ type: 'SET_LOADING', isLoading: false });
        dispatch({ type: 'SET_ACTIVE_TOOL_CALL', toolCall: null });
        break;
    }
  }, []);

  // WebSocket connection
  const { connectionState, send } = useWebSocket({
    stackId,
    customerId,
    onMessage: handleWebSocketMessage,
  });

  // Actions
  const sendMessage = useCallback((content: string) => {
    if (!content.trim()) return;

    dispatch({ type: 'SET_LOADING', isLoading: true });
    dispatch({ type: 'SET_ERROR', error: null });

    // Add user message optimistically
    const tempId = `temp_${Date.now()}`;
    const userMessage: Message = {
      id: tempId,
      conversationId: state.activeConversation?.id || '',
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    };

    // Track in ref for sync (handles race condition)
    pendingTempMessagesRef.current.set(tempId, { tempId, content });
    console.log('[Chat] Tracking pending message:', tempId);

    dispatch({ type: 'ADD_MESSAGE', message: userMessage });

    // Send via WebSocket
    send({
      type: 'send_message',
      conversationId: state.activeConversation?.id,
      content,
    });
  }, [send, state.activeConversation]);

  const startNewConversation = useCallback(() => {
    send({ type: 'start_conversation' });
    dispatch({ type: 'SET_MESSAGES', messages: [] });
  }, [send]);

  const selectConversation = useCallback((conversationId: string) => {
    send({ type: 'get_conversation', conversationId });
  }, [send]);

  const endConversation = useCallback(() => {
    if (state.activeConversation) {
      send({ type: 'end_conversation', conversationId: state.activeConversation.id });
    }
  }, [send, state.activeConversation]);

  const value: ChatContextValue = {
    ...state,
    connectionState,
    sendMessage,
    startNewConversation,
    selectConversation,
    endConversation,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

// Hook
export function useChatContext() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
}
